import ApiService from '../services/apiService';
import AuthService from '../services/authService';
import moment from 'moment';

const USE_SYTELINE_STORE = true;
const QUEUE_PROCESS_TIMER_MS = 500;
const POLL_INTERVAL_MS = 30000;

let dashboardData = {
    projNum: null,
    dashboard: null,
    ctcTiming: null
};
let cookieName = null;
let queuedUpdates = [];

// Base state for three-way merge (snapshot at load/save time)
let baseState = null;

// Polling state
let pollTimer = null;
let pollProjNum = null;
let lastKnownModified = null;
let externalChange = null;

processQueueTimer();

function setCookieName(projNum) {
    cookieName = 'dd' + projNum.trim();
}

function resetData(projNum) {
    dashboardData = {
        projNum: projNum,
        dashboard: null,
        ctcTiming: null
    };
    setCookieName(projNum);

    // Clear any stale queue entry for this project so restore doesn't trigger a false save
    let queueIdx = queuedUpdates.findIndex(element => element.projNum === projNum);
    if (queueIdx > -1) {
        queuedUpdates.splice(queueIdx, 1);
    }

    snapshotBaseState();
}

function snapshotBaseState() {
    baseState = JSON.parse(JSON.stringify(dashboardData));
}

function hasUnsavedChanges() {
    if (!baseState) return false;
    if (!baseState.projNum || !dashboardData.projNum) return false;
    if (baseState.projNum.trim() !== dashboardData.projNum.trim()) return false;
    return JSON.stringify(dashboardData) !== JSON.stringify(baseState);
}

function restoreData(projNum) {
    return new Promise((resolve, reject) => {
        resetData(projNum);

        // Backward compatibility check
        let olderCookieName = 'md' + projNum.trim();
        let olderCookieData = localStorage.getItem(olderCookieName);
        if (olderCookieData) {
            try {
                let olderData = JSON.parse(olderCookieData);
                dashboardData.ctcTiming = olderData;

                // Store this new one
                save();

                // SMOKE the old one
                localStorage.removeItem(olderCookieName);
            }
            catch {
                // Oh well
            }
        }

        if (!dashboardData.ctcTiming) {
            if (USE_SYTELINE_STORE) {
                restoreFromServer(projNum).then(() => {
                    snapshotBaseState();
                    resolve(dashboardData);
                })
            }
            else {
                let currentCookieData = localStorage.getItem(cookieName);
                if (currentCookieData) {
                    try {
                        dashboardData = JSON.parse(currentCookieData);
                    }
                    catch (err) {
                        console.error('Error accessing project data:', err)
                    }
                }
                snapshotBaseState();
                resolve(dashboardData);
            }
        }
        else {
            snapshotBaseState();
            resolve(dashboardData);
        }
    })
}

function restoreFromServer(projNum) {
    return new Promise((resolve, reject) => {
        getLatestFromServer(projNum).then((serverDashboardData) => {

            console.debug('getLatestFromServer returned: ', serverDashboardData)

            if (serverDashboardData && serverDashboardData.data) {
                dashboardData = serverDashboardData.data;
                dashboardData.projNum = projNum;  // Fix any dashboard data corruption
            }
            else {
                resetData(projNum);
            }

            // Track our custom timestamp from inside the data blob
            lastKnownModified = dashboardData.lastModified || null;

            resolve(dashboardData);
        })
        .catch((err) => {
            reject(err);
        })
    })
}

function getLatestFromServer(projNum) {
    return new Promise((resolve, reject) => {
        let serverDashboardData = {
            _ItemId: null,
            data: null,
        };

        ApiService.getProjectDashboardData(projNum).then((dashboardDataRes) => {
            if (dashboardDataRes && dashboardDataRes.length > 0) {
                try {
                    serverDashboardData._ItemId = dashboardDataRes[0]._ItemId;
                    serverDashboardData.data = JSON.parse(dashboardDataRes[0].Data);
                    if (serverDashboardData.data.version !== 2) {
                        serverDashboardData.data.dashboard.billingsForecastData = null;
                    }
                }
                catch (err) {
                    console.error('Problem with dashboard data from server: ', err)
                }
            }
            resolve(serverDashboardData);
        })
        .catch((err) => {
            reject(err);
        })
    })
}

// ========================================
// Three-way merge
// ========================================

function mergeDashboardData(base, local, server, modifiedBy) {
    if (!base || !local || !server) return local;

    let merged = JSON.parse(JSON.stringify(local));
    let fields = ['meetingTitle', 'coToCome', 'meetingTotalContract', 'meetingEstGP',
        'meetingEstGPPct', 'revenueFromTitle', 'revenueFrom', 'meetingRevenueFrom'];

    fields.forEach(field => {
        let baseVal = (base && base[field]) || '';
        let localVal = (local && local[field]) || '';
        let serverVal = (server && server[field]) || '';

        let localChanged = String(localVal) !== String(baseVal);
        let serverChanged = String(serverVal) !== String(baseVal);

        if (localChanged && serverChanged && String(localVal) !== String(serverVal)) {
            // Conflict
            let keep = window.confirm(
                `"${field}" was changed by ${modifiedBy || 'another user'}.\n` +
                `Their value: ${serverVal}\nYour value: ${localVal}\n\nKeep your value?`
            );
            merged[field] = keep ? localVal : serverVal;
        }
        else if (serverChanged && !localChanged) {
            // Take server's change
            merged[field] = serverVal;
        }
        // else: local changed or neither changed â€” keep local (already in merged)
    });

    return merged;
}

function mergeCTCTimingData(base, local, server, modifiedBy) {
    if (!base || !local || !server) return local;
    if (!Array.isArray(local) || !Array.isArray(server)) return local;

    let merged = JSON.parse(JSON.stringify(local));
    let monthNames = [];
    for (let i = 0; i < 12; i++) {
        monthNames.push(moment().add(i, 'months').format('MMM'));
    }

    local.forEach((localRow, rowIdx) => {
        let costcode = localRow.costcode;
        let baseRow = base.find(r => r.costcode === costcode);
        let serverRow = server.find(r => r.costcode === costcode);

        if (!baseRow || !serverRow || !localRow.monthsData || !serverRow.monthsData) return;

        for (let m = 0; m < 12; m++) {
            let baseVal = (baseRow.monthsData && baseRow.monthsData[m]) || '';
            let localVal = (localRow.monthsData && localRow.monthsData[m]) || '';
            let serverVal = (serverRow.monthsData && serverRow.monthsData[m]) || '';

            let localChanged = String(localVal) !== String(baseVal);
            let serverChanged = String(serverVal) !== String(baseVal);

            if (localChanged && serverChanged && String(localVal) !== String(serverVal)) {
                // Conflict
                let keep = window.confirm(
                    `Cost code ${costcode}, ${monthNames[m]}: ` +
                    `Changed by ${modifiedBy || 'another user'} to $${serverVal}.\n` +
                    `Your value: $${localVal}\n\nKeep your value?`
                );
                merged[rowIdx].monthsData[m] = keep ? localVal : serverVal;
            }
            else if (serverChanged && !localChanged) {
                // Take server's change
                merged[rowIdx].monthsData[m] = serverVal;
            }
        }
    });

    return merged;
}

function mergeData(local, serverData) {
    if (!baseState || !serverData || !serverData.data) return local;

    let server = serverData.data;
    let modifiedBy = server.lastModifiedBy || 'another user';
    let merged = JSON.parse(JSON.stringify(local));

    merged.dashboard = mergeDashboardData(
        baseState.dashboard, local.dashboard, server.dashboard, modifiedBy
    );
    merged.ctcTiming = mergeCTCTimingData(
        baseState.ctcTiming, local.ctcTiming, server.ctcTiming, modifiedBy
    );

    return merged;
}

// ========================================
// Polling for external changes
// ========================================

function startPolling(projNum) {
    stopPolling();
    pollProjNum = projNum;
    externalChange = null;

    pollTimer = setInterval(() => {
        // Don't poll while saving
        if (pendingUpdates()) return;
        if (!pollProjNum) return;

        ApiService.getProjectDashboardData(pollProjNum).then((res) => {
            if (res && res.length > 0) {
                try {
                    let serverData = JSON.parse(res[0].Data);
                    let serverModified = serverData.lastModified;
                    let serverModifiedBy = serverData.lastModifiedBy;
                    let currentUser = AuthService.getUsername();

                    if (lastKnownModified &&
                        serverModified !== lastKnownModified &&
                        serverModifiedBy &&
                        serverModifiedBy.toLowerCase() !== (currentUser || '').toLowerCase()) {

                        externalChange = {
                            updatedBy: serverModifiedBy,
                            recordDate: serverModified
                        };
                    }
                } catch (err) {
                    // Data parse error, skip
                }
            }
        }).catch(() => {
            // Silently ignore poll failures
        });
    }, POLL_INTERVAL_MS);
}

function stopPolling() {
    if (pollTimer) {
        clearInterval(pollTimer);
        pollTimer = null;
    }
    pollProjNum = null;
}

function getExternalChange() {
    return externalChange;
}

function clearExternalChange() {
    externalChange = null;
}

// ========================================
// Queue and save
// ========================================

function putIntoQueue(dashboardData) {
    dashboardData.version = 2;
    let copyOfDashboardDataStr = JSON.stringify(dashboardData);
    let idx = queuedUpdates.findIndex(element => element.projNum === dashboardData.projNum);

    if (idx > -1) {
        let queueItem = queuedUpdates[idx];
        if (queueItem.dashboardDataStr !== copyOfDashboardDataStr) {
            queueItem.needsUpdate = true;
        }
        queueItem.dashboardDataStr = copyOfDashboardDataStr;
    }
    else {
        queuedUpdates.push({
            projNum: dashboardData.projNum,
            needsUpdate: true,
            isUpdating: false,
            dashboardDataStr: copyOfDashboardDataStr
        });
    }
}

function processQueueTimer() {
    setTimeout(processQueue, QUEUE_PROCESS_TIMER_MS);
}

async function processQueue() {
    for (let i=0; i < queuedUpdates.length; i++) {
        let item = queuedUpdates[i];
        if (item.needsUpdate) {
            console.debug(`Uploading ${dashboardData.projNum} dashboard...`)
            item.needsUpdate = false;
            item.isUpdating = true;

            let localData = JSON.parse(item.dashboardDataStr);
            let serverResult = await getLatestFromServer(localData.projNum);

            // Check if someone else modified the data using our custom timestamp
            let currentUser = AuthService.getUsername();
            let serverModified = serverResult.data ? serverResult.data.lastModified : null;
            let serverModifiedBy = serverResult.data ? serverResult.data.lastModifiedBy : null;
            let serverChangedByOther = lastKnownModified &&
                serverModified !== lastKnownModified &&
                serverModifiedBy &&
                serverModifiedBy.toLowerCase() !== (currentUser || '').toLowerCase();

            let dataToSave = localData;
            if (serverChangedByOther && serverResult.data) {
                // Three-way merge
                dataToSave = mergeData(localData, serverResult);
            }

            // Stamp our own modification tracking
            let now = new Date().toISOString();
            dataToSave.lastModified = now;
            dataToSave.lastModifiedBy = currentUser;

            await ApiService.updateDashboardData(localData.projNum, dataToSave, serverResult._ItemId);

            lastKnownModified = now;
            dashboardData = dataToSave;
            snapshotBaseState();
            clearExternalChange();

            item.isUpdating = false;
            console.debug(`Uploading ${localData.projNum} dashboard complete.`)
        }
    }
    processQueueTimer();
}

function save() {
    if (cookieName) {
        localStorage.setItem(cookieName, JSON.stringify(dashboardData));
    }

    if (USE_SYTELINE_STORE) {
        putIntoQueue(dashboardData);
    }
}

function saveDashboardData(data) {
    dashboardData.dashboard = data;
    save();
}

function saveCTCTimingData(data) {
    dashboardData.ctcTiming = data;
    save();
}

function clearData(projNum) {
    setCookieName(projNum);
    localStorage.removeItem(cookieName);
}

function pendingUpdates() {
    return queuedUpdates.find(item => item.needsUpdate || item.isUpdating);
}

export default {
    restoreData: restoreData,
    saveDashboardData: saveDashboardData,
    saveCTCTimingData: saveCTCTimingData,
    clearData: clearData,
    pendingUpdates: pendingUpdates,
    startPolling: startPolling,
    stopPolling: stopPolling,
    getExternalChange: getExternalChange,
    clearExternalChange: clearExternalChange,
    hasUnsavedChanges: hasUnsavedChanges
}
